import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, SkipBack, SkipForward, Settings } from "lucide-react";

/* Adaptive video player.
 * - Uses native HTML5 video for HLS-style ranges via /api/stream proxy.
 * - Quality auto-detects via network info; user can override.
 * - Custom overlay controls for cinematic feel.
 * - Sends watch-time heartbeats to /api/videos/:id/track when videoId provided.
 */
export default function VideoPlayer({
  src,
  poster,
  autoPlay = false,
  vertical = false,
  onEnded,
  onPlayEvt,
  onPauseEvt,
  onTimeEvt,
  onErrorEvt,
  loop = false,
  videoId = null,
  controlsRef = null,
  minimalChrome = false,
}) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(autoPlay);
  const [muted, setMuted] = useState(autoPlay);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [quality, setQuality] = useState("auto");
  const hideTimer = useRef(null);

  // Imperative handle for parent (used by TerminalPlayer postMessage bridge)
  useEffect(() => {
    if (!controlsRef) return;
    controlsRef.current = {
      play: () => videoRef.current?.play(),
      pause: () => videoRef.current?.pause(),
      seek: (t) => { if (videoRef.current && isFinite(t)) videoRef.current.currentTime = t; },
      setMuted: (m) => { if (videoRef.current) { videoRef.current.muted = !!m; setMuted(!!m); } },
      setVolume: (x) => { if (videoRef.current) { const v = Math.max(0, Math.min(1, x)); videoRef.current.volume = v; setVolume(v); } },
      getState: () => {
        const v = videoRef.current;
        return v ? { currentTime: v.currentTime, duration: v.duration, paused: v.paused, muted: v.muted, volume: v.volume } : null;
      },
    };
  }, [controlsRef]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      setProgress(v.currentTime);
      setDuration(v.duration || 0);
      if (onTimeEvt) onTimeEvt({ currentTime: v.currentTime, duration: v.duration || 0 });
    };
    const onPlay = () => { setPlaying(true); if (onPlayEvt) onPlayEvt(); };
    const onPause = () => { setPlaying(false); if (onPauseEvt) onPauseEvt(); };
    const onErr = (e) => { if (onErrorEvt) onErrorEvt(e); };
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onTime);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("error", onErr);
    if (onEnded) v.addEventListener("ended", onEnded);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onTime);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("error", onErr);
      if (onEnded) v.removeEventListener("ended", onEnded);
    };
  }, [onEnded, onPlayEvt, onPauseEvt, onTimeEvt, onErrorEvt]);

  // Adaptive quality hint based on connection
  useEffect(() => {
    const c = navigator.connection;
    if (!c) return;
    const t = setTimeout(() => {
      if (c.effectiveType === "4g" && c.downlink > 5) setQuality("hd");
      else if (c.effectiveType === "4g") setQuality("sd");
      else setQuality("low");
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // Watch-time heartbeat
  useEffect(() => {
    if (!videoId) return;
    const sessionId = `${Math.random().toString(36).slice(2)}_${Date.now()}`;
    let lastBeat = 0;
    let lastPos = 0;
    let totalWatched = 0;
    let timer = null;

    const sendBeat = (completed = false) => {
      const v = videoRef.current;
      if (!v) return;
      const now = v.currentTime;
      const elapsed = Math.max(0, Math.min(now - lastBeat, 60));
      if (elapsed < 1 && !completed) return;
      totalWatched += elapsed;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const lang = navigator.language;
      // Persistent anonymous viewer ID → returning-viewer detection.
      let viewer_id = null;
      try {
        viewer_id = localStorage.getItem("nexus_viewer_id");
        if (!viewer_id) {
          viewer_id = `nv_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
          localStorage.setItem("nexus_viewer_id", viewer_id);
        }
      } catch (_) { /* private mode / blocked */ }
      // UTM harvest: pull from the CURRENT URL if present, otherwise reuse
      // whatever we stashed in sessionStorage on landing. This survives
      // internal navigation (which strips ?utm_* from the address bar).
      const utms = {};
      try {
        const url = new URL(window.location.href);
        for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
          const q = url.searchParams.get(key);
          if (q) {
            utms[key] = q;
            sessionStorage.setItem(`nexus_${key}`, q);
          } else {
            const stashed = sessionStorage.getItem(`nexus_${key}`);
            if (stashed) utms[key] = stashed;
          }
        }
      } catch (_) { /* URL parsing safe-fail */ }
      import("../lib/api").then(({ default: client }) => {
        client.post(`/videos/${videoId}/track`, {
          session_id: sessionId,
          elapsed_seconds: elapsed,
          position: now,
          duration: v.duration || 0,
          completed,
          timezone: tz,
          language: lang,
          screen_w: window.screen?.width,
          screen_h: window.screen?.height,
          referrer: document.referrer || null,
          viewer_id,
          ...utms,
        }).catch(() => {});
      });
      lastBeat = now;
      lastPos = now;
    };

    timer = setInterval(() => {
      const v = videoRef.current;
      if (v && !v.paused && !v.ended) sendBeat();
    }, 10000); // every 10s

    const v = videoRef.current;
    const onEnd = () => sendBeat(true);
    if (v) v.addEventListener("ended", onEnd);
    const onUnload = () => sendBeat();
    window.addEventListener("beforeunload", onUnload);

    return () => {
      clearInterval(timer);
      if (v) v.removeEventListener("ended", onEnd);
      window.removeEventListener("beforeunload", onUnload);
      sendBeat();
    };
  }, [videoId]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };

  const seek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    v.currentTime = parseFloat(e.target.value);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const setVol = (e) => {
    const v = videoRef.current;
    if (!v) return;
    const x = parseFloat(e.target.value);
    v.volume = x;
    setVolume(x);
    if (x > 0 && v.muted) { v.muted = false; setMuted(false); }
  };

  const fullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  const skip = (sec) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min((v.duration || 0), v.currentTime + sec));
  };

  const onMouseMove = () => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowControls(false), 2500);
  };

  const fmt = (s) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const ss = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${ss}`;
  };

  return (
    <div
      ref={containerRef}
      data-testid="video-player"
      onMouseMove={onMouseMove}
      onClick={togglePlay}
      className={`relative w-full bg-black overflow-hidden rounded-xl ${vertical ? "aspect-[9/16]" : "aspect-video"}`}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay={autoPlay}
        muted={autoPlay || muted}
        playsInline
        loop={loop}
        preload="metadata"
        className="w-full h-full object-contain"
        data-testid="video-element"
      />
      {/* Center play */}
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-20 h-20 rounded-full bg-[#FF6B00]/90 flex items-center justify-center aura-pulse">
            <Play className="w-8 h-8 text-black fill-black ml-1" />
          </div>
        </div>
      )}

      {/* Controls */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`absolute inset-x-0 bottom-0 px-4 pb-3 pt-10 bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-opacity ${showControls ? "opacity-100" : "opacity-0"}`}
      >
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.01}
          value={progress}
          onChange={seek}
          className="player-slider w-full"
          data-testid="player-progress"
        />
        <div className="mt-2 flex items-center gap-3 text-white text-sm">
          <button data-testid="player-play" onClick={togglePlay} className="hover:text-[#FFB800]">
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button data-testid="player-back10" onClick={() => skip(-10)} className="hover:text-[#FFB800]"><SkipBack className="w-4 h-4" /></button>
          <button data-testid="player-fwd10" onClick={() => skip(10)} className="hover:text-[#FFB800]"><SkipForward className="w-4 h-4" /></button>
          <button data-testid="player-mute" onClick={toggleMute} className="hover:text-[#FFB800]">
            {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <input type="range" min={0} max={1} step={0.05} value={volume} onChange={setVol} className="player-slider w-20 hidden sm:block" />
          <span className="font-mono text-xs text-zinc-300">{fmt(progress)} / {fmt(duration)}</span>
          <div className="flex-1" />
          <span className="hidden md:flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#FFB800]">
            <Settings className="w-3.5 h-3.5" /> {quality.toUpperCase()}
          </span>
          <button data-testid="player-fullscreen" onClick={fullscreen} className="hover:text-[#FFB800]"><Maximize className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
